import { c as _c } from "react/compiler-runtime";
import { IcoForbid40 } from '@onefootprint/icons';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Divider, MultiSelect } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceInsightEvent } from '@/entities/components/details/hooks/use-entity-device-insights';
import MapComponent from '../map';
import MapMarker from '../map/components/map-marker';
import AddressCard from './components/address-card';
import AddressType from './components/address-card/types';
import ContainerWithToggle from './components/container-with-toggle';
import InsightEventCard from './components/insight-event-card';
import useEntries from './hooks/use-entries';
import useMultiSelectOptions from './hooks/use-multi-select-options';
export type ContentProps = {
  entity: Entity;
  events: DeviceInsightEvent[];
};
const Content = t0 => {
  const $ = _c(51);
  const {
    entity,
    events
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [detailsHidden, setDetailsHidden] = useState(false);
  const {
    allOptions,
    selectedOptions,
    handleOptionsChange
  } = useMultiSelectOptions(entity, events);
  const {
    entries,
    selectedCoords,
    selectedId,
    onSelectedIdChange
  } = useEntries(entity, events, selectedOptions);
  let cards;
  let markers;
  if ($[1] !== entity || $[2] !== entries || $[3] !== onSelectedIdChange || $[4] !== selectedId || $[5] !== t) {
    cards = [];
    markers = [];
    Object.entries(entries).forEach(t2 => {
      const [id, entry] = t2;
      const isSelected = selectedId === id;
      if (entry.type === "businessAddress" || entry.type === "residentialAddress") {
        const type = entry.type === "businessAddress" ? AddressType.business : AddressType.residential;
        cards.push(<AddressCard id={id} key={id} type={type} entity={entity} isPending={entry.cardisPending} isSelected={isSelected} onSelect={onSelectedIdChange} />);
      } else {
        if (entry.data) {
          cards.push(<InsightEventCard id={id} key={id} data={entry.data} isSelected={isSelected} onSelect={onSelectedIdChange} />);
        }
      }
      if (entry.marker) {
        markers.push(<MapMarker key={id} id={id} lat={entry.marker.lat} lng={entry.marker.lng} getIcon={entry.marker.getIcon} isSelected={isSelected} />);
      }
    });
    if (!cards.length) {
      let t3;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = <IcoForbid40 />;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      let t4;
      if ($[9] !== t) {
        t4 = t(_temp);
        $[9] = t;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      let t5;
      if ($[11] !== t4) {
        t5 = <span className="text-label-2 text-primary">{t4}</span>;
        $[11] = t4;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      let t6;
      if ($[13] !== t) {
        t6 = t(_temp2);
        $[13] = t;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      let t7;
      if ($[15] !== t6) {
        t7 = <span className="text-body-2 text-primary text-center">{t6}</span>;
        $[15] = t6;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      let t8;
      if ($[17] !== t5 || $[18] !== t7) {
        t8 = <div key="empty" className="flex flex-col gap-5 justify-center items-center p-4 grow w-full h-full">{t3}<div className="flex flex-col gap-2 justify-center items-center w-full">{t5}{t7}</div></div>;
        $[17] = t5;
        $[18] = t7;
        $[19] = t8;
      } else {
        t8 = $[19];
      }
      cards.push(t8);
    }
    $[1] = entity;
    $[2] = entries;
    $[3] = onSelectedIdChange;
    $[4] = selectedId;
    $[5] = t;
    $[6] = cards;
    $[7] = markers;
  } else {
    cards = $[6];
    markers = $[7];
  }
  let t2;
  if ($[20] !== onSelectedIdChange) {
    t2 = id_0 => {
      onSelectedIdChange(id_0);
      const card = document.getElementById(`device-insights-card-${id_0}`);
      const parent = card?.parentElement;
      if (card && parent) {
        parent.scrollTo({
          top: card.offsetTop,
          behavior: "smooth"
        });
      }
    };
    $[20] = onSelectedIdChange;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  const handleSelectFromMap = t2;
  let t3;
  if ($[22] !== t) {
    t3 = t(_temp3);
    $[22] = t;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== selectedOptions) {
    t4 = selectedOptions.map(_temp4);
    $[24] = selectedOptions;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== allOptions || $[27] !== handleOptionsChange || $[28] !== t3 || $[29] !== t4) {
    t5 = <MultiSelect placeholder={t3} options={allOptions} value={t4} onChange={handleOptionsChange} />;
    $[26] = allOptions;
    $[27] = handleOptionsChange;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider className="mt-2" />;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== cards) {
    t7 = <div className="relative flex flex-col gap-3 h-full overflow-y-scroll box-content w-full pt-2 pb-3">{cards}</div>;
    $[32] = cards;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== detailsHidden || $[35] !== t5 || $[36] !== t7) {
    t8 = <ContainerWithToggle isHidden={detailsHidden} onChangeHidden={setDetailsHidden}>{t5}{t6}{t7}</ContainerWithToggle>;
    $[34] = detailsHidden;
    $[35] = t5;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const t9 = !detailsHidden;
  let t10;
  if ($[38] !== t9) {
    t10 = cx("w-full h-56 shrink-0 rounded-md overflow-hidden relative border border-tertiary transition-transform duration-300 ease-in-out translate-x-0 md:h-full", {
      "md:translate-x-[214px]": t9
    });
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  const t11 = !detailsHidden;
  let t12;
  if ($[40] !== handleSelectFromMap || $[41] !== markers || $[42] !== selectedCoords) {
    t12 = <MapComponent markers={markers} selectedCoords={selectedCoords} onSelect={handleSelectFromMap} />;
    $[40] = handleSelectFromMap;
    $[41] = markers;
    $[42] = selectedCoords;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t12) {
    t13 = <div className={t10} data-has-overlay={t11}>{t12}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t13 || $[49] !== t8) {
    t14 = <div className="flex flex-col-reverse h-[640px] w-full rounded-md overflow-hidden relative border border-tertiary md:block md:h-[584px]">{t8}{t13}</div>;
    $[48] = t13;
    $[49] = t8;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
};
export default Content;
function _temp($) {
  return $.empty.title;
}
function _temp2($_0) {
  return $_0.empty.description;
}
function _temp3($_1) {
  return $_1.select.placeholder;
}
function _temp4(option) {
  return option.value;
}